import { Button } from 'reactstrap';
import moment from 'moment';

import DatePickerField from 'Lib/components/form/DatePickerField';
import Form from 'Lib/components/form/Form';
import isFunction from 'Lib/utilities/isFunction';

import { useFilter } from './useFilter';

const DATE_FORMAT = 'YYYY-MM-DD';

const DateRange = props => {
    const { reduxData } = props.context;

    const handleClear = form => {
        if (form) {
            form.setFieldValue('minDate', null);
            form.setFieldValue('maxDate', null);
        }

        setFilterValues([
            { name: minName, value: null },
            { name: maxName, value: null }
        ]);

        setValue(null);
    };

    const handleCancel = () => {
        handleToggle();
    };

    const { minName = 'minDate', maxName = 'maxDate' } = props;

    const filter = useFilter(
        props,
        () => {
            const minValue = props.context.getFilterValue(minName);
            const maxValue = props.context.getFilterValue(maxName);

            if (minValue) {
                bindValue(minValue, maxValue);
            }
        },
        handleClear,
        handleCancel
    );

    const { value, setValue, handleToggle, setFilterValues, renderMenuButtons } = filter;
    const { options } = props;

    const handleSubmit = values => {
        const { minDate, maxDate } = values;

        bindValue(minDate, maxDate);
        setFilterValues([
            { name: minName, value: minDate },
            { name: maxName, value: maxDate }
        ]);
        handleToggle();
    };

    const bindValue = (minDate, maxDate) => {
        let label = null;

        if (minDate && !maxDate) {
            label = `From ${minDate}`;
        }

        if (maxDate && !minDate) {
            label = `To ${maxDate}`;
        }

        if (minDate && maxDate) {
            label = `${moment(minDate).format('MM-DD-YYYY')} - ${moment(maxDate).format('MM-DD-YYYY')}`;
        }

        setValue({ value: { minDate, maxDate }, label });
    };

    const handleQuickSelectChange = (option, form) => {
        const { onSelected } = option;

        if (isFunction(onSelected)) {
            const values = onSelected();
            if (values.min) {
                form.setFieldValue('minDate', values.min);
            }

            if (values.max) {
                form.setFieldValue('maxDate', values.max);
            }
            form.submitForm();
        } else {
            if (onSelected) {
                form.setFieldValue('minDate', onSelected);
                form.setFieldValue('maxDate', onSelected);
                form.submitForm();
            }
        }
    };

    const calcLastXDays = days => {
        return {
            min: moment().subtract(days, 'days').format(DATE_FORMAT),
            max: moment().format(DATE_FORMAT)
        };
    };

    const calcNextXDays = days => {
        return {
            min: moment().format(DATE_FORMAT),
            max: moment().add(days, 'days').format(DATE_FORMAT)
        };
    };

    const calcToDate = type => ({
        min: moment().startOf(type).format(DATE_FORMAT),
        max: moment().format(DATE_FORMAT)
    });

    const calcCurrentPeriod = () => ({
        min: reduxData.accountingSettings.currentPeriod.startDate,
        max: reduxData.accountingSettings.currentPeriod.endDate
    });

    const calcPreviousPeriod = () => ({
        min: reduxData.accountingSettings.previousPeriod.startDate,
        max: reduxData.accountingSettings.previousPeriod.endDate
    });

    const quickFilters = [
        { label: 'Today', value: 'today', onSelected: moment().format('YYYY-MM-DD') },
        { label: 'Last 7 Days', value: 'last7Days', onSelected: () => calcLastXDays(7) },
        { label: 'Last 30 Days', value: 'last30Days', onSelected: () => calcLastXDays(30) },
        { label: 'Last 60 Days', value: 'last60Days', onSelected: () => calcLastXDays(60) },
        { label: 'Last 90 Days', value: 'last90Days', onSelected: () => calcLastXDays(90) },
        { label: 'Next 7 Days', value: 'next30Days', onSelected: () => calcNextXDays(7) },
        { label: 'Next 30 Days', value: 'next30Days', onSelected: () => calcNextXDays(30) },
        { label: 'Next 60 Days', value: 'next60Days', onSelected: () => calcNextXDays(60) },
        { label: 'Next 90 Days', value: 'next90Days', onSelected: () => calcNextXDays(90) },
        { label: 'Week to Date', value: 'weekToDate', onSelected: () => calcToDate('week') },
        { label: 'Month to Date', value: 'monthToDate', onSelected: () => calcToDate('month') },
        { label: 'Year to Date', value: 'yearToDate', onSelected: () => calcToDate('year') }
    ];

    if (reduxData && !reduxData.isLoading) {
        quickFilters.push(
            { label: 'Use Current Period', value: 'currentPeriod', onSelected: calcCurrentPeriod },
            { label: 'Use Previous Period', value: 'previousPeriod', onSelected: calcPreviousPeriod }
        );
    }

    return filter.render(
        <div
            className="searchable-list-filter-menu"
            style={{
                padding: '10px',
                minWidth: '500px'
            }}
        >
            <Form
                initialValues={{
                    minDate: value?.value?.minDate,
                    maxDate: value?.value?.maxDate
                }}
                onSubmit={handleSubmit}
                render={form => {
                    return (
                        <form onSubmit={form.handleSubmit}>
                            <DatePickerField name={'minDate'} label={'From'} options={options} />
                            <DatePickerField name={'maxDate'} label={'To'} options={options} />
                            <div
                                style={{
                                    marginBottom: '1rem'
                                }}
                            >
                                {quickFilters.map((quickFilter, quickFilterIndex) => {
                                    return (
                                        <Button
                                            key={quickFilterIndex}
                                            color="info"
                                            outline
                                            type="button"
                                            size="sm"
                                            onClick={() => {
                                                handleQuickSelectChange(quickFilter, form);
                                            }}
                                            style={{
                                                marginBottom: '0.5rem',
                                                marginRight: '0.5rem'
                                            }}
                                        >
                                            {quickFilter.label}
                                        </Button>
                                    );
                                })}
                            </div>
                            {renderMenuButtons({ handleCancel, handleClear, form })}
                        </form>
                    );
                }}
            />
        </div>
    );
};

export default DateRange;
